import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Container, Table, Form, Button, Row, Col, Badge, Modal, InputGroup } from 'react-bootstrap';
import FundTransferModal from '../../../components/modals/FundTransferModal';
import { showToast } from '../../../utils/toast';

const FundTransfer = () => {
  // Mock data for Fund transfers
  const mockBeneficiaryData = useMemo(
    () => ({
      '9876543210': {
        name: 'Rajesh Kumar',
        limit: '₹2,00,000',
        beneficiaries: [
          {
            accountNumber: '1234567890',
            name: 'Sanjay Mehta',
            ifscCode: 'SBIN0001234',
            isVerified: true
          },
          {
            accountNumber: '1234098765',
            name: 'Kavita Sharma',
            ifscCode: 'HDFC0005678',
            isVerified: false
          }
        ]
      },
      '9123456789': {
        name: 'Amit Sharma',
        limit: '₹1,50,000',
        beneficiaries: [
          {
            accountNumber: '2345678901',
            name: 'Pooja Verma',
            ifscCode: 'ICIC0003456',
            isVerified: true
          }
        ]
      },
      '9988776655': {
        name: 'Priya Singh',
        limit: '₹50,000',
        beneficiaries: [
          {
            accountNumber: '3456789012',
            name: 'Rohit Gupta',
            ifscCode: 'PUNB0009988',
            isVerified: false
          }
        ]
      },
      '9999999999': {
        name: 'Demo User',
        limit: '₹75,000',
        beneficiaries: [
          {
            accountNumber: '111122223333',
            name: 'Sample Beneficiary',
            ifscCode: 'UTIB0000001',
            isVerified: true
          },
          {
            accountNumber: '444455556666',
            name: 'Placeholder Beneficiary',
            ifscCode: 'SBIN0000001',
            isVerified: false
          }
        ]
      }
    }),
    []
  );

  const mockAccountData = useMemo(
    () => ({
      '1234567890': {
        name: 'Rajesh Kumar',
        mobile: '9876543210',
        limit: '₹2,00,000',
        beneficiaries: [
          {
            accountNumber: '7894561230',
            name: 'Anita Verma',
            ifscCode: 'HDFC0001123',
            isVerified: true
          },
          {
            accountNumber: '6549873210',
            name: 'Sandeep Roy',
            ifscCode: 'ICIC0005566',
            isVerified: false
          }
        ]
      },
      '2345678901': {
        name: 'Amit Sharma',
        mobile: '9123456789',
        limit: '₹1,50,000',
        beneficiaries: [
          {
            accountNumber: '9087654321',
            name: 'Tina Kapoor',
            ifscCode: 'SBIN0004455',
            isVerified: true
          }
        ]
      },
      '3456789012': {
        name: 'Priya Singh',
        mobile: '9988776655',
        limit: '₹50,000',
        beneficiaries: [
          {
            accountNumber: '8765432190',
            name: 'Manish Patel',
            ifscCode: 'PUNB0007788',
            isVerified: false
          }
        ]
      }
    }),
    []
  );

  const bankOptions = useMemo(
    () => [
      { name: 'State Bank of India', ifsc: 'SBIN0000001' },
      { name: 'HDFC Bank', ifsc: 'HDFC0000001' },
      { name: 'ICICI Bank', ifsc: 'ICIC0000001' },
      { name: 'Axis Bank', ifsc: 'UTIB0000001' },
      { name: 'Punjab National Bank', ifsc: 'PUNB0000001' }
    ],
    []
  );

  const accountHolderDirectory = useMemo(
    () => ({
      '1234567890': { name: 'Sanjay Mehta', bank: 'State Bank of India' },
      '1234098765': { name: 'Kavita Sharma', bank: 'HDFC Bank' },
      '2345678901': { name: 'Pooja Verma', bank: 'ICICI Bank' },
      '3456789012': { name: 'Rohit Gupta', bank: 'Punjab National Bank' },
      '111122223333': { name: 'Sample Beneficiary', bank: 'Axis Bank' },
      '444455556666': { name: 'Placeholder Beneficiary', bank: 'State Bank of India' },
      '7894561230': { name: 'Anita Verma', bank: 'HDFC Bank' },
      '6549873210': { name: 'Sandeep Roy', bank: 'ICICI Bank' },
      '9087654321': { name: 'Tina Kapoor', bank: 'State Bank of India' },
      '8765432190': { name: 'Manish Patel', bank: 'Punjab National Bank' }
    }),
    []
  );
  const defaultAddressDirectory = useMemo(
    () => ({
      '9876543210': 'Flat 201, Sunrise Apartments, MG Road, Mumbai, Maharashtra - 400001',
      '9123456789': 'House 14-B, Green Park, New Delhi - 110016',
      '9988776655': 'Shyam Sadan, Ring Road, Jaipur, Rajasthan - 302001',
      '9999999999': 'Plot 9, Sector 18, Noida, Uttar Pradesh - 201301',
      '1234567890': 'B-102, Lake View Residency, Ahmedabad, Gujarat - 380015',
      '2345678901': '2nd Floor, Ganga Towers, Pune, Maharashtra - 411001',
      '3456789012': '101, Skyline Plaza, Bengaluru, Karnataka - 560001'
    }),
    []
  );

  const [beneficiaryMap, setBeneficiaryMap] = useState(mockBeneficiaryData);
  const [accountBeneficiaryMap, setAccountBeneficiaryMap] = useState(mockAccountData);
  const [data, setData] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEmptyBeneficiaries, setShowEmptyBeneficiaries] = useState(false);

  // State management
  const [transferType, setTransferType] = useState('mobile'); // 'mobile' or 'account'
  const [mobileNumber, setMobileNumber] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [inputError, setInputError] = useState('');
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedContactInfo, setSelectedContactInfo] = useState('');
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [activeBeneficiary, setActiveBeneficiary] = useState(null);
  const [transferForm, setTransferForm] = useState({ amount: '', confirmAmount: '', tpin: '' });
  const [transferErrors, setTransferErrors] = useState({});
  const [isTpinVisible, setIsTpinVisible] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [transferReceipt, setTransferReceipt] = useState(null);
  const [amountInWords, setAmountInWords] = useState('');
  const handlePendingBeneficiaryNameChange = (value) => {
    setPendingBeneficiary((prev) => (prev ? { ...prev, name: value } : prev));
  };
  const receiptRef = useRef(null);
  const transferAmountRef = useRef(null);
  const confirmAmountRef = useRef(null);
  const transferTpinRef = useRef(null);
  const [showAddBeneficiaryModal, setShowAddBeneficiaryModal] = useState(false);
  const [addBeneficiaryForm, setAddBeneficiaryForm] = useState({
    bank: '',
    ifsc: '',
    accountNumber: '',
    verification: true,
    holderName: ''
  });
  const [addBeneficiaryErrors, setAddBeneficiaryErrors] = useState({});
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [pendingBeneficiary, setPendingBeneficiary] = useState(null);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [registrationForm, setRegistrationForm] = useState({ name: '', address: '' });
  const [registrationErrors, setRegistrationErrors] = useState({});
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [otpError, setOtpError] = useState('');
  const [shouldAutoOpenAddBeneficiary, setShouldAutoOpenAddBeneficiary] = useState(false);

  const companyInfo = {
    name: 'WebDekho Fintech',
    logo: '/24-pay-logo.svg'
  };

  // Column definitions
  const columns = [
    { key: 'accountNumber', label: 'Account No' },
    { key: 'name', label: 'Name' },
    { key: 'ifscCode', label: 'IFSC' },
    { key: 'isVerified', label: 'Verification Status' },
    { key: 'actions', label: 'Action' }
  ];

  const getPrefilledAddress = (contactKey) => {
    if (!contactKey) {
      return 'House No. 101, WebDekho Residency, Mumbai, Maharashtra - 400001';
    }

    return (
      defaultAddressDirectory[contactKey] ||
      'House No. 101, WebDekho Residency, Mumbai, Maharashtra - 400001'
    );
  };

  const shouldHideSearchUI =
    selectedUser &&
    selectedUser.name !== '—' &&
    (data.length > 0 || showEmptyBeneficiaries);
  const displayLimit =
    selectedUser && selectedUser.limit && selectedUser.limit !== '—' ? selectedUser.limit : null;

  // Validate phone number
  const validatePhone = (phone) => {
    const phoneRegex = /^[6-9]\d{9}$/;
    return phoneRegex.test(phone);
  };

  // Validate account number
  const validateAccount = (account) => {
    return account.length >= 9 && account.length <= 18;
  };

  // Handle transfer type change
  const handleTransferTypeChange = (type) => {
    setTransferType(type);
    setMobileNumber('');
    setAccountNumber('');
    setInputError('');
    setSelectedUser(null);
    setData([]);
    setShowEmptyBeneficiaries(false);
  };

  // Handle mobile number change
  const handleMobileChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Only digits
    if (value.length <= 10) {
      setMobileNumber(value);
      setInputError('');
    }
  };

  // Handle account number change
  const handleAccountChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Only digits
    if (value.length <= 18) {
      setAccountNumber(value);
      setInputError('');
    }
  };

  // Handle submit
  const handleSubmit = () => {
    if (transferType === 'mobile') {
      if (!mobileNumber) {
        setInputError('Please enter mobile number');
        return;
      }
      
      if (!validatePhone(mobileNumber)) {
        setInputError('Invalid mobile number (10 digits starting with 6-9)');
        return;
      }

      setInputError('');

      const record = beneficiaryMap[mobileNumber];

      if (record) {
        setSelectedUser({
          name: record.name,
          mobile: mobileNumber,
          limit: record.limit,
          key: mobileNumber,
          keyType: 'mobile'
        });
        setData(record.beneficiaries);
        setShowEmptyBeneficiaries(record.beneficiaries.length === 0);
        setShowRegistrationModal(false);
        setShowOtpModal(false);
      } else {
        const contactKey = mobileNumber;
        setSelectedUser({
          name: '—',
          mobile: mobileNumber,
          limit: '—',
          key: mobileNumber,
          keyType: 'mobile'
        });
        setData([]);
        setShowEmptyBeneficiaries(false);
        setRegistrationForm({ name: '', address: getPrefilledAddress(contactKey) });
        setRegistrationErrors({});
        setShowRegistrationModal(true);
      }

      setSelectedContactInfo(mobileNumber);
      setShowModal(false);
    } else {
      if (!accountNumber) {
        setInputError('Please enter account number');
        return;
      }
      
      if (!validateAccount(accountNumber)) {
        setInputError('Invalid account number (9-18 digits required)');
        return;
      }

      setInputError('');

      const accountRecord = accountBeneficiaryMap[accountNumber];

      if (accountRecord) {
        setSelectedUser({
          name: accountRecord.name,
          mobile: accountRecord.mobile || accountNumber,
          limit: accountRecord.limit,
          key: accountNumber,
          keyType: 'account'
        });
        setData(accountRecord.beneficiaries);
        setShowEmptyBeneficiaries(accountRecord.beneficiaries.length === 0);
        setShowRegistrationModal(false);
        setShowOtpModal(false);
      } else {
        setSelectedUser(null);
        setData([]);
        setShowEmptyBeneficiaries(false);
        setShowRegistrationModal(false);
        showToast.info('Account number not found. Please verify the details.');
      }

      setSelectedContactInfo(accountNumber);
      setShowModal(false);
    }
  };

  // Handle modal close
  const handleCloseModal = () => {
    setShowModal(false);
  };

  // Handle transfer submission
  const handleTransferSubmit = (transferData) => {
    const newBeneficiary = {
      accountNumber: transferData.accountNumber,
      name: transferData.beneficiaryName,
      ifscCode: transferData.ifscCode.toUpperCase(),
      isVerified: false
    };

    if (selectedUser) {
      const identifier = selectedUser.key || selectedUser.mobile;
      const updateMap = selectedUser.keyType === 'account' ? setAccountBeneficiaryMap : setBeneficiaryMap;
      const filteredExisting = data.filter((item) => item.accountNumber !== newBeneficiary.accountNumber);
      const updatedBeneficiaries = [newBeneficiary, ...filteredExisting];

      updateMap((prev) => {
        const current = prev[identifier] || {
          name: selectedUser.name,
          mobile: selectedUser.mobile,
          limit: selectedUser.limit,
          beneficiaries: []
        };

        return {
          ...prev,
          [identifier]: {
            ...current,
            beneficiaries: updatedBeneficiaries
          }
        };
      });

      if (
        selectedUser.keyType === 'account' &&
        selectedUser.mobile &&
        beneficiaryMap[selectedUser.mobile]
      ) {
        setBeneficiaryMap((prev) => {
          const current = prev[selectedUser.mobile] || {
            name: selectedUser.name,
            mobile: selectedUser.mobile,
            limit: selectedUser.limit,
            beneficiaries: []
          };

          const filtered = current.beneficiaries.filter(
            (item) => item.accountNumber !== newBeneficiary.accountNumber
          );

          return {
            ...prev,
            [selectedUser.mobile]: {
              ...current,
              beneficiaries: [newBeneficiary, ...filtered]
            }
          };
        });
      }

      setData(updatedBeneficiaries);
    }

    setMobileNumber('');
    setAccountNumber('');
  };

  const handleVerifyToggle = (index) => {
    setData((prev) => {
      const updated = prev.map((item, itemIndex) =>
        itemIndex === index ? { ...item, isVerified: !item.isVerified } : item
      );

      if (selectedUser) {
        const identifier = selectedUser.key || selectedUser.mobile;
        const updateMap = selectedUser.keyType === 'account' ? setAccountBeneficiaryMap : setBeneficiaryMap;

        updateMap((mapPrev) => {
          const current = mapPrev[identifier];
          if (!current) {
            return mapPrev;
          }

          const updatedBeneficiaries = current.beneficiaries.map((beneficiary, itemIndex) =>
            itemIndex === index ? { ...beneficiary, isVerified: !beneficiary.isVerified } : beneficiary
          );

          return {
            ...mapPrev,
            [identifier]: {
              ...current,
              beneficiaries: updatedBeneficiaries
            }
          };
        });

        if (
          selectedUser.keyType === 'account' &&
          selectedUser.mobile &&
          beneficiaryMap[selectedUser.mobile]
        ) {
          setBeneficiaryMap((mapPrev) => {
            const current = mapPrev[selectedUser.mobile];
            if (!current) {
              return mapPrev;
            }

            return {
              ...mapPrev,
              [selectedUser.mobile]: {
                ...current,
                beneficiaries: updated
              }
            };
          });
        }
      }

      return updated;
    });
  };

  const openAddBeneficiaryModal = useCallback((user) => {
    if (!user) return;
    setAddBeneficiaryForm({
      bank: '',
      ifsc: '',
      accountNumber: '',
      verification: true,
      holderName: ''
    });
    setAddBeneficiaryErrors({});
    setPendingBeneficiary(null);
    setSelectedContactInfo(user.key || user.mobile || '');
    setShowAddBeneficiaryModal(true);
  }, []);

  useEffect(() => {
    if (shouldAutoOpenAddBeneficiary && selectedUser) {
      openAddBeneficiaryModal(selectedUser);
      setShouldAutoOpenAddBeneficiary(false);
    }
  }, [shouldAutoOpenAddBeneficiary, selectedUser, openAddBeneficiaryModal]);

  const handleAddBeneficiary = () => {
    if (!selectedUser) return;
    openAddBeneficiaryModal(selectedUser);
  };

  const handleBackToSearch = () => {
    setSelectedUser(null);
    setData([]);
    setMobileNumber('');
    setAccountNumber('');
    setInputError('');
    setSelectedContactInfo('');
    setShowEmptyBeneficiaries(false);
  };

  const handleAddBeneficiaryModalClose = () => {
    setShowAddBeneficiaryModal(false);
    setAddBeneficiaryForm({
      bank: '',
      ifsc: '',
      accountNumber: '',
      verification: true,
      verification: true,
      holderName: ''
    });
    setAddBeneficiaryErrors({});
  };

  const handleRegistrationModalClose = () => {
    setShowRegistrationModal(false);
    setRegistrationForm({ name: '', address: '' });
    setRegistrationErrors({});
  };

  const handleRegistrationChange = (e) => {
    const { name, value } = e.target;
    setRegistrationForm((prev) => ({
      ...prev,
      [name]: value
    }));

    if (registrationErrors[name]) {
      setRegistrationErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateRegistrationForm = () => {
    const errors = {};

    if (!registrationForm.name.trim()) {
      errors.name = 'Enter name';
    }

    if (!registrationForm.address.trim()) {
      errors.address = 'Enter address';
    }

    setRegistrationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleRegistrationSubmit = () => {
    if (!validateRegistrationForm()) {
      return;
    }

    handleRegistrationModalClose();
    setOtpDigits(['', '', '', '', '', '']);
    setOtpError('');
    setShowOtpModal(true);
  };

  const handleOtpChange = (index, value) => {
    const digit = value.replace(/\D/g, '');

    setOtpDigits((prev) => {
      const updated = [...prev];
      updated[index] = digit.slice(-1);
      return updated;
    });

    if (digit && index < 5) {
      const nextInput = document.getElementById(`otp-input-${index + 1}`);
      if (nextInput) {
        nextInput.focus();
      }
    }

    if (!digit && index > 0) {
      const prevInput = document.getElementById(`otp-input-${index - 1}`);
      if (prevInput) {
        prevInput.focus();
      }
    }

    if (otpError) {
      setOtpError('');
    }
  };

  const handleOtpKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !otpDigits[index] && index > 0) {
      const prevInput = document.getElementById(`otp-input-${index - 1}`);
      if (prevInput) {
        prevInput.focus();
      }
    }
  };

  const handleOtpSubmit = () => {
    const otpValue = otpDigits.join('');

    if (otpValue.length !== 6) {
      setOtpError('Enter 6-digit OTP');
      return;
    }

    showToast.success('Registration completed successfully');
    const contactKey =
      selectedContactInfo ||
      selectedUser?.key ||
      (transferType === 'mobile' ? mobileNumber : accountNumber);
    const keyType =
      selectedUser?.keyType || (transferType === 'account' ? 'account' : 'mobile');
    const newName = registrationForm.name || 'New Sender';
    const defaultLimit = selectedUser?.limit && selectedUser.limit !== '—' ? selectedUser.limit : '₹0';
    const newSelectedUser = {
      name: newName,
      mobile:
        keyType === 'mobile'
          ? contactKey
          : selectedUser?.mobile || selectedContactInfo || mobileNumber || accountNumber,
      limit: defaultLimit,
      key: contactKey || '',
      keyType
    };

    setSelectedUser(newSelectedUser);
    setData([]);
    setShowEmptyBeneficiaries(true);

    if (contactKey) {
      if (keyType === 'mobile') {
        setBeneficiaryMap((prev) => ({
          ...prev,
          [contactKey]: {
            name: newName,
            mobile: contactKey,
            limit: defaultLimit,
            beneficiaries: []
          }
        }));
      } else {
        setAccountBeneficiaryMap((prev) => ({
          ...prev,
          [contactKey]: {
            name: newName,
            mobile: newSelectedUser.mobile,
            limit: defaultLimit,
            beneficiaries: []
          }
        }));
      }
    }

    setRegistrationForm({ name: '', address: '' });
    setRegistrationErrors({});
    setShouldAutoOpenAddBeneficiary(true);
    handleOtpClose();
  };

  const handleOtpClose = () => {
    setShowOtpModal(false);
    setOtpDigits(['', '', '', '', '', '']);
    setOtpError('');
  };

  const handleAddBeneficiaryChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'bank') {
      const selectedBank = bankOptions.find((bank) => bank.name === value);
      setAddBeneficiaryForm((prev) => ({
        ...prev,
        bank: value,
        ifsc: selectedBank ? selectedBank.ifsc : ''
      }));
      if (addBeneficiaryErrors.bank) {
        setAddBeneficiaryErrors((prev) => ({ ...prev, bank: '' }));
      }
      return;
    }

    if (name === 'verification') {
      setAddBeneficiaryForm((prev) => ({
        ...prev,
        verification: type === 'checkbox' ? checked : value === 'true',
        holderName:
          type === 'checkbox' ? (checked ? '' : prev.holderName) : value === 'true' ? '' : prev.holderName
      }));
      if (checked && addBeneficiaryErrors.holderName) {
        setAddBeneficiaryErrors((prev) => ({ ...prev, holderName: '' }));
      }
      return;
    }

    if (name === 'accountNumber') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 18);
      setAddBeneficiaryForm((prev) => ({
        ...prev,
        accountNumber: digitsOnly
      }));
      if (addBeneficiaryErrors.accountNumber) {
        setAddBeneficiaryErrors((prev) => ({ ...prev, accountNumber: '' }));
      }
      return;
    }
    if (name === 'ifsc') {
    const formattedIfsc = value.toUpperCase().slice(0, 11);
    setAddBeneficiaryForm((prev) => ({
      ...prev,
      ifsc: formattedIfsc
    }));
    return;
  }

    if (name === 'holderName') {
      setAddBeneficiaryForm((prev) => ({
        ...prev,
        holderName: value
      }));
      if (addBeneficiaryErrors.holderName) {
        setAddBeneficiaryErrors((prev) => ({ ...prev, holderName: '' }));
      }
      return;
    }
  };

  const validateAddBeneficiaryForm = () => {
    const errors = {};

    if (!addBeneficiaryForm.bank) {
      errors.bank = 'Please select a bank';
    }

    if (!addBeneficiaryForm.accountNumber) {
      errors.accountNumber = 'Enter account number';
    } else if (addBeneficiaryForm.accountNumber.length < 9) {
      errors.accountNumber = 'Account number must be 9-18 digits';
    }

    if (!addBeneficiaryForm.verification && !addBeneficiaryForm.holderName.trim()) {
      errors.holderName = 'Enter beneficiary name';
    }

    setAddBeneficiaryErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddBeneficiarySearch = () => {
    if (!validateAddBeneficiaryForm()) {
      return;
    }

    if (!addBeneficiaryForm.verification) {
      handleAddBeneficiaryModalClose();
      return;
    }

    const selectedBank = bankOptions.find((bank) => bank.name === addBeneficiaryForm.bank);
    const accountDetails = accountHolderDirectory[addBeneficiaryForm.accountNumber] || {};

    const resolvedName = accountDetails.name || addBeneficiaryForm.holderName || 'Manoj Tiwari';
    const beneficiaryDetails = {
      name: resolvedName,
      originalName: resolvedName,
      bank: selectedBank ? selectedBank.name : accountDetails.bank || '—',
      accountNumber: addBeneficiaryForm.accountNumber,
      ifsc: addBeneficiaryForm.ifsc || selectedBank?.ifsc || ''
    };

    setPendingBeneficiary(beneficiaryDetails);
    setShowAddBeneficiaryModal(false);
    setShowVerificationModal(true);
  };

  const handleVerificationCancel = () => {
    setShowVerificationModal(false);
    setPendingBeneficiary(null);
  };

  const handleVerificationConfirm = () => {
    if (!pendingBeneficiary || !selectedUser) {
      handleVerificationCancel();
      return;
    }

    const newBeneficiary = {
      accountNumber: pendingBeneficiary.accountNumber,
      name: pendingBeneficiary.name,
      ifscCode: pendingBeneficiary.ifsc?.toUpperCase() || '',
      isVerified: true
    };

    setData((prev) => {
      const filtered = prev.filter((item) => item.accountNumber !== newBeneficiary.accountNumber);
      return [newBeneficiary, ...filtered];
    });

    const identifier = selectedUser.key || selectedUser.mobile;
    const updateMap = selectedUser.keyType === 'account' ? setAccountBeneficiaryMap : setBeneficiaryMap;

    updateMap((prev) => {
      const current = prev[identifier] || {
        name: selectedUser.name,
        mobile: selectedUser.mobile,
        limit: selectedUser.limit,
        beneficiaries: []
      };

      const filtered = current.beneficiaries.filter(
        (item) => item.accountNumber !== newBeneficiary.accountNumber
      );

      return {
        ...prev,
        [identifier]: {
          ...current,
          beneficiaries: [newBeneficiary, ...filtered]
        }
      };
    });

    if (
      selectedUser.keyType === 'account' &&
      selectedUser.mobile &&
      beneficiaryMap[selectedUser.mobile]
    ) {
      setBeneficiaryMap((prev) => {
        const current = prev[selectedUser.mobile] || {
          name: selectedUser.name,
          mobile: selectedUser.mobile,
          limit: selectedUser.limit,
          beneficiaries: []
        };

        const filtered = current.beneficiaries.filter(
          (item) => item.accountNumber !== newBeneficiary.accountNumber
        );

        return {
          ...prev,
          [selectedUser.mobile]: {
            ...current,
            beneficiaries: [newBeneficiary, ...filtered]
          }
        };
      });
    }

    showToast.success('Beneficiary verified successfully');

    setShowVerificationModal(false);
    setPendingBeneficiary(null);
    handleTransferClick(newBeneficiary);
  };

  const handleTransferClick = (beneficiary) => {
    setActiveBeneficiary(beneficiary);
    setTransferForm({ amount: '', confirmAmount: '', tpin: '' });
    setTransferErrors({});
    setIsTpinVisible(false);
    setShowTransferModal(true);
    setTimeout(() => {
      transferAmountRef.current?.focus();
    }, 150);
  };

  const handleTransferModalClose = () => {
    setShowTransferModal(false);
    setActiveBeneficiary(null);
    setTransferForm({ amount: '', confirmAmount: '', tpin: '' });
    setTransferErrors({});
    setIsTpinVisible(false);
  };

  const handleReceiptClose = () => {
    setShowReceiptModal(false);
    setTransferReceipt(null);
    setSelectedUser(null);
    setData([]);
    setShowEmptyBeneficiaries(false);
    setMobileNumber('');
    setAccountNumber('');
    setSelectedContactInfo('');
  };

  const handlePrintReceipt = () => {
    if (!receiptRef.current) {
      return;
    }

    const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
      .map((node) => node.outerHTML)
      .join('');

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.title = 'Transfer Receipt Print Preview';

    document.body.appendChild(iframe);

    const printDocument = iframe.contentWindow?.document;

    if (!printDocument) {
      document.body.removeChild(iframe);
      showToast.error?.('Unable to prepare receipt for printing.');
      return;
    }

    printDocument.open();
    printDocument.write('<html><head><title>Transfer Receipt</title>');
    printDocument.write(styles);
    printDocument.write('</head><body style="margin:0;padding:16px;font-family:inherit;">');
    printDocument.write(receiptRef.current.innerHTML);
    printDocument.write('</body></html>');
    printDocument.close();

    const triggerPrint = () => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
      setTimeout(() => {
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
      }, 0);
    };

    if (iframe.contentWindow?.document.readyState === 'complete') {
      triggerPrint();
    } else {
      iframe.onload = triggerPrint;
    }
  };

  const handleTransferFormChange = (e) => {
    const { name, value } = e.target;
    let sanitizedValue = value;

    if (name === 'tpin') {
      sanitizedValue = value.replace(/\D/g, '').slice(0, 4);
    } else if (name === 'amount' || name === 'confirmAmount') {
      sanitizedValue = value.replace(/[^\d.]/g, '');
    }

    setTransferForm((prev) => {
      const updatedForm = { ...prev, [name]: sanitizedValue };
      if (name === 'amount') {
        updatedForm.confirmAmount = '';
      }
      return updatedForm;
    });

    setTransferErrors((prev) => {
      if (!prev[name] && !(name === 'amount' && prev.confirmAmount)) {
        return prev;
      }
      const updatedErrors = { ...prev };
      delete updatedErrors[name];
      if (name === 'amount') {
        delete updatedErrors.confirmAmount;
      }
      return updatedErrors;
    });
  };

  const validateTransferForm = () => {
    const errors = {};

    const amountValue = Number(transferForm.amount);

    if (!transferForm.amount || Number.isNaN(amountValue) || amountValue <= 0) {
      errors.amount = 'Enter a valid amount';
    }

    if (amountValue > 1000) {
      const confirmValue = Number(transferForm.confirmAmount);
      if (!transferForm.confirmAmount) {
        errors.confirmAmount = 'Confirm transfer amount';
      } else if (Number.isNaN(confirmValue) || confirmValue !== amountValue) {
        errors.confirmAmount = 'Amount mismatch';
      }
    }

    if (!transferForm.tpin || transferForm.tpin.length !== 4) {
      errors.tpin = 'Enter 4-digit T-PIN';
    }

    setTransferErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleTransferSend = () => {
    if (!validateTransferForm()) {
      return;
    }

    showToast.success('Transfer Successfully');

    const generateAmountBreakup = (totalAmount) => {
      const amountValue = Number(totalAmount);
      if (!amountValue || amountValue <= 0) {
        return [];
      }

      const breakup = [];
      let remaining = amountValue;
      const chunkSize = 5000;
      let counter = 1;

      while (remaining > 0) {
        const partAmount = Math.min(chunkSize, remaining);
        breakup.push({
          amount: partAmount,
          transactionId: `TXNSPLIT${Date.now()}${counter}`,
          bankRef: `BNK${Math.floor(Math.random() * 900000) + 100000}`
        });
        remaining -= partAmount;
        counter += 1;
      }

      return breakup;
    };

    const receipt = {
      transactionId: `TXN${Date.now()}`,
      timestamp: new Date().toLocaleString(),
      senderName: selectedUser?.name || '—',
      senderMobile: selectedUser?.mobile || selectedContactInfo,
      beneficiaryName: activeBeneficiary?.name || '',
      beneficiaryMobile: activeBeneficiary?.mobile || '',
      beneficiaryAccount: activeBeneficiary?.accountNumber || '',
      beneficiaryIfsc: activeBeneficiary?.ifscCode || '',
      amount: transferForm.amount,
      amountBreakup: generateAmountBreakup(transferForm.amount)
    };

    const numberToWords = (num) => {
      const a = [
        '',
        'One',
        'Two',
        'Three',
        'Four',
        'Five',
        'Six',
        'Seven',
        'Eight',
        'Nine',
        'Ten',
        'Eleven',
        'Twelve',
        'Thirteen',
        'Fourteen',
        'Fifteen',
        'Sixteen',
        'Seventeen',
        'Eighteen',
        'Nineteen'
      ];
      const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
      const g = ['', 'Thousand', 'Lakh', 'Crore'];

      if (!num || Number.isNaN(Number(num))) return '';

      let n = Math.round(Number(num));
      const groups = [];
      while (n > 0) {
        groups.push(n % 1000);
        n = Math.floor(n / 1000);
      }

      const words = groups
        .map((group, index) => {
          if (!group) return '';

          const hundreds = Math.floor(group / 100);
          const remainder = group % 100;
          const tens = Math.floor(remainder / 10);
          const ones = remainder % 10;

          let segment = '';
          if (hundreds) segment += `${a[hundreds]} Hundred `;
          if (remainder) {
            if (remainder < 20) {
              segment += `${a[remainder]} `;
            } else {
              segment += `${b[tens]} `;
              if (ones) segment += `${a[ones]} `;
            }
          }

          if (segment) segment += `${g[index]} `;
          return segment.trim();
        })
        .filter(Boolean)
        .reverse()
        .join(' ');

      return words ? `${words} Only` : '';
    };

    setTransferReceipt(receipt);
    setAmountInWords(numberToWords(transferForm.amount));

    handleTransferModalClose();
    setShowReceiptModal(true);
  };

  return (
    <Container fluid className="p-4">
      <div className="user-table-container">
        {!shouldHideSearchUI && (
          <>
            {/* Header with Title */}
            <Row className="mb-4">
              <Col>
                <h4 className="mb-0" style={{ color: '#1e293b', fontWeight: '700', fontSize: '1.5rem' }}>
                  <i className="bi bi-cash-stack me-2"></i>
                  Fund Transfer
                </h4>
              </Col>
            </Row>

            {/* Transfer Type Tabs */}
            <Row className="mb-3">
              <Col lg={6} md={8} sm={12}>
                <div className="d-flex gap-2 mb-3">
                  <Button
                    onClick={() => handleTransferTypeChange('mobile')}
                    style={{
                      padding: '0.6rem 1.5rem',
                      fontWeight: '600',
                      background: transferType === 'mobile' ? '#667eea' : 'transparent',
                      border: '2px solid #667eea',
                      color: transferType === 'mobile' ? 'white' : '#667eea',
                      transition: 'all 0.3s ease',
                      borderRadius: '8px'
                    }}
                  >
                    By Mobile
                  </Button>
                  <Button
                    onClick={() => handleTransferTypeChange('account')}
                    style={{
                      padding: '0.6rem 1.5rem',
                      fontWeight: '600',
                      background: transferType === 'account' ? '#667eea' : 'transparent',
                      border: '2px solid #667eea',
                      color: transferType === 'account' ? 'white' : '#667eea',
                      transition: 'all 0.3s ease',
                      borderRadius: '8px'
                    }}
                  >
                    By Account
                  </Button>
                </div>
              </Col>
            </Row>

            {/* Input Section */}
            <Row className="mb-4">
              <Col lg={6} md={8} sm={12}>
                <Form.Group>
                  <Form.Label style={{ fontWeight: '600', color: '#1e293b' }}>
                    {transferType === 'mobile' ? 'Enter Mobile Number' : 'Enter Account Number'} <span className="text-danger">*</span>
                  </Form.Label>
                  <div className="d-flex gap-2">
                    {transferType === 'mobile' ? (
                      <Form.Control
                        type="tel"
                        placeholder="Enter 10-digit mobile number"
                        value={mobileNumber}
                        onChange={handleMobileChange}
                        isInvalid={!!inputError}
                        maxLength={10}
                        style={{ fontSize: '1rem' }}
                      />
                    ) : (
                      <Form.Control
                        type="tel"
                        placeholder="Enter account number"
                        value={accountNumber}
                        onChange={handleAccountChange}
                        isInvalid={!!inputError}
                        maxLength={18}
                        style={{ fontSize: '1rem' }}
                      />
                    )}
                    <Button 
                      onClick={handleSubmit}
                      disabled={
                        transferType === 'mobile' 
                          ? mobileNumber.length !== 10 
                          : accountNumber.length < 9
                      }
                      className="btn-outline-submit"
                      style={{ 
                        minWidth: '120px',
                        fontWeight: '600',
                        background: 'transparent',
                        border: '2px solid #667eea',
                        color: '#667eea',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (!e.currentTarget.disabled) {
                          e.currentTarget.style.background = '#667eea';
                          e.currentTarget.style.color = 'white';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = '#667eea';
                      }}
                    >
                      Submit
                    </Button>
                  </div>
                  {inputError && (
                    <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                      {inputError}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>
          </>
        )}

        {shouldHideSearchUI && (
          <Row className="mb-3">
            <Col>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleBackToSearch}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  borderRadius: '8px',
                  fontWeight: '600'
                }}
              >
                <i className="bi bi-arrow-left-short" style={{ fontSize: '1.2rem' }}></i>
                Back to Search
              </Button>
            </Col>
          </Row>
        )}

        {selectedUser && selectedUser.name !== '—' && (
          <div
            className="mb-4"
            style={{
              background: '#f5f7ff',
              borderRadius: '18px',
              padding: '1.75rem',
              border: '1px solid rgba(102,126,234,0.18)',
              boxShadow: '0 16px 40px rgba(102,126,234,0.15)'
            }}
          >
            <Row className="g-4 align-items-center">
              <Col xl={3} lg={4} md={6} sm={12} className="mb-3 mb-xl-0">
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem'
                  }}
                >
                  <div
                    style={{
                      width: '42px',
                      height: '42px',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, rgba(102,126,234,0.18) 0%, rgba(118,75,162,0.24) 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#4c51bf',
                      fontSize: '1.2rem'
                    }}
                  >
                    <i className="bi bi-person-fill"></i>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase', color: '#6b7280' }}>
                      Name
                    </div>
                    <div style={{ fontSize: '1.05rem', fontWeight: 600, color: '#1f2937' }}>{selectedUser.name}</div>
                  </div>
                </div>
              </Col>
              <Col xl={3} lg={4} md={6} sm={12} className="mb-3 mb-xl-0">
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem'
                  }}
                >
                  <div
                    style={{
                      width: '42px',
                      height: '42px',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, rgba(102,126,234,0.18) 0%, rgba(118,75,162,0.24) 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#4c51bf',
                      fontSize: '1.2rem'
                    }}
                  >
                    <i className="bi bi-phone"></i>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase', color: '#6b7280' }}>
                      Mobile
                    </div>
                    <div style={{ fontSize: '1.05rem', fontWeight: 600, color: '#1f2937' }}>
                      {selectedUser.mobile}
                    </div>
                  </div>
                </div>
              </Col>
              <Col xl={3} lg={4} md={6} sm={12} className="mb-3 mb-xl-0">
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem'
                  }}
                >
                  <div
                    style={{
                      width: '42px',
                      height: '42px',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, rgba(102,126,234,0.18) 0%, rgba(118,75,162,0.24) 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#4c51bf',
                      fontSize: '1.2rem'
                    }}
                  >
                    <i className="bi bi-wallet2"></i>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase', color: '#6b7280' }}>
                      Limit
                    </div>
                    <div style={{ fontSize: '1.05rem', fontWeight: 600, color: '#1f2937' }}>{selectedUser.limit}</div>
                  </div>
                </div>
              </Col>
              <Col xl={3} lg={12} md={6} sm={12} className="text-xl-end text-lg-end">
                <Button
                  onClick={handleAddBeneficiary}
                  disabled={!selectedUser || selectedUser.name === '—'}
                  style={{
                    padding: '0.55rem 1.4rem',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    borderRadius: '10px',
                    background: 'linear-gradient(135deg, #667eea 0%, #5a67d8 100%)',
                    border: 'none',
                    color: '#fff',
                    boxShadow: '0 10px 24px rgba(102,126,234,0.3)',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (!e.currentTarget.disabled) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 14px 28px rgba(102,126,234,0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 10px 24px rgba(102,126,234,0.3)';
                  }}
                >
                  <i className="bi bi-person-plus me-2"></i>
                  Add Beneficiaries
                </Button>
              </Col>
            </Row>
          </div>
        )}

        {(data.length > 0 || showEmptyBeneficiaries) && (
          <div className="table-responsive" style={{ background: '#f0f2f5', borderRadius: '12px', padding: '0' }}>
            <Table
              bordered
              hover
              className="modern-table"
              style={{ background: '#f0f2f5', fontSize: '0.9rem' }}
            >
              <thead style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                <tr>
                  {columns.map((column) => (
                    <th key={column.key}>{column.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.length > 0 ? (
                  data.map((row, index) => (
                    <tr
                      key={index}
                      style={{
                        background: '#f0f2f5',
                        cursor: row.isVerified ? 'pointer' : 'default'
                      }}
                      onClick={() => {
                        if (row.isVerified) {
                          handleTransferClick(row);
                        }
                      }}
                    >
                      <td>{row.accountNumber}</td>
                      <td>{row.name}</td>
                      <td>{row.ifscCode}</td>
                      <td>
                        <Badge bg={row.isVerified ? 'success' : 'danger'}>
                          {row.isVerified ? 'Verified' : 'Pending Verification'}
                        </Badge>
                      </td>
                      <td>
                        <Button
                          size="sm"
                          variant={row.isVerified ? 'outline-success' : 'outline-secondary'}
                          onClick={(event) => {
                            event.stopPropagation();
                            if (row.isVerified) {
                              handleTransferClick(row);
                            } else {
                              handleVerifyToggle(index);
                            }
                          }}
                        >
                          {row.isVerified ? 'Transfer' : 'Mark Verified'}
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={columns.length} className="text-center py-4 text-muted">
                      No beneficiaries added yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        )}

        <Modal show={showAddBeneficiaryModal} onHide={handleAddBeneficiaryModalClose} centered>
          <Modal.Header closeButton>
            <Modal.Title>Add Beneficiary</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>
                  Select Bank <span style={{ color: '#dc2626' }}>*</span>
                </Form.Label>
                <Form.Select
                  name="bank"
                  value={addBeneficiaryForm.bank}
                  onChange={handleAddBeneficiaryChange}
                  isInvalid={!!addBeneficiaryErrors.bank}
                >
                  <option value="">Select bank</option>
                  {bankOptions.map((bank) => (
                    <option key={bank.ifsc} value={bank.name}>
                      {bank.name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {addBeneficiaryErrors.bank}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  IFS Code <span style={{ color: '#dc2626' }}>*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="ifsc"
                  placeholder="Enter IFS Code"
                  value={addBeneficiaryForm.ifsc}
                  onChange={handleAddBeneficiaryChange}
                />
                <div className="mt-1" style={{ fontSize: '0.75rem', color: '#dc2626' }}>
                  Note: This is the master IFS Code. You can modify it.
                </div>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  Account Number <span style={{ color: '#dc2626' }}>*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="accountNumber"
                  placeholder="Enter account number"
                  value={addBeneficiaryForm.accountNumber}
                  onChange={handleAddBeneficiaryChange}
                  isInvalid={!!addBeneficiaryErrors.accountNumber}
                  maxLength={18}
                />
                <Form.Control.Feedback type="invalid">
                  {addBeneficiaryErrors.accountNumber}
                </Form.Control.Feedback>
              </Form.Group>

              <div className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="beneficiary-verification"
                  name="verification"
                  label="Verification"
                  checked={addBeneficiaryForm.verification}
                  onChange={handleAddBeneficiaryChange}
                  style={{ marginBottom: '0.35rem' }}
                />
                <span
                  style={{
                    color: '#dc2626',
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    display: 'inline-block'
                  }}
                >
                  Recommended
                </span>
              </div>

              {!addBeneficiaryForm.verification && (
                <Form.Group className="mb-3">
                  <Form.Label>
                    Beneficiary Name <span style={{ color: '#dc2626' }}>*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="holderName"
                    placeholder="Enter beneficiary name"
                    value={addBeneficiaryForm.holderName}
                    onChange={handleAddBeneficiaryChange}
                    isInvalid={!!addBeneficiaryErrors.holderName}
                  />
                  <Form.Control.Feedback type="invalid">
                    {addBeneficiaryErrors.holderName}
                  </Form.Control.Feedback>
                </Form.Group>
              )}
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleAddBeneficiaryModalClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleAddBeneficiarySearch}
              disabled={!addBeneficiaryForm.bank || !addBeneficiaryForm.accountNumber}
            >
              Submit
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showVerificationModal} onHide={handleVerificationCancel} centered>
          <Modal.Header closeButton style={{ borderBottom: 'none', padding: '1rem 1.4rem' }}>
            <Modal.Title style={{ fontSize: '1.05rem', fontWeight: 600 }}>Verify Beneficiary</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ background: '#f9fafb', padding: '1.2rem 1.4rem' }}>
            {pendingBeneficiary && (
              <div
                style={{
                  background: '#fff',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 6px 16px rgba(15, 23, 42, 0.05)',
                  padding: '1rem 1.25rem'
                }}
              >
                <div
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: '#64748b',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    marginBottom: '1rem',
                    textAlign: 'center'
                  }}
                >
                  Beneficiary Details
                </div>

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    padding: '0.6rem 0',
                    fontSize: '0.9rem',
                    gap: '1rem',
                    borderBottom: '1px solid #f1f5f9'
                  }}
                >
                  <span style={{ color: '#475569', fontWeight: 600, paddingTop: '0.35rem' }}>Customer Name</span>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      gap: '0.35rem',
                      maxWidth: '60%'
                    }}
                  >
                    <Form.Control
                      type="text"
                      value={pendingBeneficiary.name || ''}
                      onChange={(e) => handlePendingBeneficiaryNameChange(e.target.value)}
                      style={{ textAlign: 'left', borderRadius: '6px', fontSize: '0.9rem' }}
                    />
                    {pendingBeneficiary.originalName &&
                      pendingBeneficiary.name?.trim() !== pendingBeneficiary.originalName && (
                        <span style={{ color: '#dc2626', fontSize: '0.75rem', fontWeight: 600, textAlign: 'right' }}>
                          Your status is changed in to unverified.
                        </span>
                      )}
                  </div>
                </div>

                {[
                  { label: 'Bank', value: pendingBeneficiary.bank },
                  { label: 'Account Number', value: pendingBeneficiary.accountNumber },
                  { label: 'IFSC Code', value: pendingBeneficiary.ifsc }
                ].map(({ label, value }, index, arr) => (
                  <div
                    key={label}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0.6rem 0',
                      fontSize: '0.9rem',
                      borderBottom: index === arr.length - 1 ? 'none' : '1px solid #f1f5f9'
                    }}
                  >
                    <span style={{ color: '#475569', fontWeight: 600, flex: '0 0 40%' }}>{label}</span>
                    <span style={{ color: '#0f172a', fontWeight: 500, flex: 1, textAlign: 'center' }}>
                      {value || '—'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer style={{ borderTop: 'none', padding: '1rem 1.4rem', background: '#f9fafb' }}>
            <Button variant="light" onClick={handleVerificationCancel}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleVerificationConfirm} style={{ fontWeight: 600 }}>
              Confirm
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showRegistrationModal} onHide={handleRegistrationModalClose} centered>
          <Modal.Header closeButton>
            <Modal.Title>Sender Registration</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>
                  Sender Name <span style={{ color: '#dc2626' }}>*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  placeholder="Enter full name"
                  value={registrationForm.name}
                  onChange={handleRegistrationChange}
                  isInvalid={!!registrationErrors.name}
                />
                <Form.Control.Feedback type="invalid">
                  {registrationErrors.name}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  Address <span style={{ color: '#dc2626' }}>*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="address"
                  placeholder="Enter address"
                  value={registrationForm.address}
                  onChange={handleRegistrationChange}
                  isInvalid={!!registrationErrors.address}
                />
                <Form.Control.Feedback type="invalid">
                  {registrationErrors.address}
                </Form.Control.Feedback>
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleRegistrationModalClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleRegistrationSubmit}>
              Register
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showOtpModal} onHide={handleOtpClose} centered>
          <Modal.Header closeButton>
            <Modal.Title>OTP Confirmation</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted mb-3">Enter the 6-digit OTP sent to the registered mobile number.</p>
            <div className="d-flex justify-content-center gap-2">
              {otpDigits.map((digit, index) => (
                <Form.Group key={index} className="text-center">
                  <Form.Control
                    id={`otp-input-${index}`}
                    type="text"
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    isInvalid={!!otpError}
                    style={{
                      width: '42px',
                      height: '48px',
                      fontSize: '1.2rem',
                      textAlign: 'center'
                    }}
                    maxLength={1}
                  />
                </Form.Group>
              ))}
            </div>
            {otpError && <div className="text-danger mt-2">{otpError}</div>}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleOtpClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleOtpSubmit}>
              Submit
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Fund Transfer Modal */}
        <FundTransferModal
          show={showModal}
          handleClose={handleCloseModal}
          transferType={transferType}
          contactInfo={selectedContactInfo}
          onSubmit={handleTransferSubmit}
        />

        <Modal show={showTransferModal} onHide={handleTransferModalClose} centered>
          <Modal.Header closeButton>
            <Modal.Title>Fund Transfer</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {activeBeneficiary && (
              <div
                className="mb-4"
                style={{
                  background: '#ffffff',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 8px 18px rgba(15, 23, 42, 0.08)',
                  padding: '1rem 1.25rem'
                }}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                    gap: '0.75rem'
                  }}
                >
                  <div
                    style={{
                      background: '#f8fafc',
                      borderRadius: '10px',
                      padding: '0.75rem',
                      border: '1px solid #e2e8f0'
                    }}
                  >
                    <div style={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>
                      Customer Name
                    </div>
                    <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#1e293b' }}>
                      {activeBeneficiary.name}
                    </div>
                  </div>
                  <div
                    style={{
                      background: '#f8fafc',
                      borderRadius: '10px',
                      padding: '0.75rem',
                      border: '1px solid #e2e8f0'
                    }}
                  >
                    <div style={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>
                      Account Number
                    </div>
                    <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#1e293b' }}>
                      {activeBeneficiary.accountNumber}
                    </div>
                  </div>
                  <div
                    style={{
                      background: '#f8fafc',
                      borderRadius: '10px',
                      padding: '0.75rem',
                      border: '1px solid #e2e8f0'
                    }}
                  >
                    <div style={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>
                      IFSC Code
                    </div>
                    <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#1e293b' }}>
                      {activeBeneficiary.ifscCode}
                    </div>
                  </div>
                  {displayLimit && (
                    <div
                      style={{
                        background: '#f8fafc',
                        borderRadius: '10px',
                        padding: '0.75rem',
                        border: '1px solid #e2e8f0'
                      }}
                    >
                      <div style={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>
                        Available Limit
                      </div>
                      <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#1e293b' }}>{displayLimit}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <Form>
              <Form.Group className="mb-3">
                <Form.Label>
                  Amount <span style={{ color: '#dc2626' }}>*</span>
                </Form.Label>
                <Form.Control
                  ref={transferAmountRef}
                  type="number"
                  name="amount"
                  placeholder="Enter amount"
                  value={transferForm.amount}
                  onChange={handleTransferFormChange}
                  isInvalid={!!transferErrors.amount}
                  min="1"
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      if (Number(transferForm.amount) > 1000) {
                        confirmAmountRef.current?.focus();
                      } else {
                        transferTpinRef.current?.focus();
                      }
                    }
                  }}
                />
                <Form.Control.Feedback type="invalid">
                  {transferErrors.amount}
                </Form.Control.Feedback>
              </Form.Group>

              {Number(transferForm.amount) > 1000 && (
                <Form.Group className="mb-3">
                  <Form.Label>
                    Re Confirm Amount <span style={{ color: '#dc2626' }}>*</span>
                  </Form.Label>
                  <Form.Control
                    ref={confirmAmountRef}
                    type="number"
                    name="confirmAmount"
                    placeholder="Re-enter amount"
                    value={transferForm.confirmAmount}
                    onChange={handleTransferFormChange}
                    isInvalid={!!transferErrors.confirmAmount}
                    min="1"
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        transferTpinRef.current?.focus();
                      }
                    }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {transferErrors.confirmAmount}
                  </Form.Control.Feedback>
                </Form.Group>
              )}

              <Form.Group className="mb-3">
                <Form.Label>
                  T-PIN <span style={{ color: '#dc2626' }}>*</span>
                </Form.Label>
                <InputGroup hasValidation>
                  <Form.Control
                    ref={transferTpinRef}
                    type={isTpinVisible ? 'text' : 'password'}
                    name="tpin"
                    placeholder="Enter 4-digit T-PIN"
                    value={transferForm.tpin}
                    onChange={handleTransferFormChange}
                    isInvalid={!!transferErrors.tpin}
                    maxLength={4}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        handleTransferSend();
                      }
                    }}
                  />
                  <Button
                    variant="outline-secondary"
                    onClick={() => setIsTpinVisible((prev) => !prev)}
                    aria-label={isTpinVisible ? 'Hide T-PIN' : 'Show T-PIN'}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '42px' }}
                  >
                    <i className={`bi ${isTpinVisible ? 'bi-eye-slash' : 'bi-eye'}`} />
                  </Button>
                </InputGroup>
                <Form.Control.Feedback type="invalid">
                  {transferErrors.tpin}
                </Form.Control.Feedback>
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer style={{ borderTop: '1px solid #eef2ff', background: '#f9fafb' }}>
            <Button variant="light" onClick={handleTransferModalClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleTransferSend} style={{ fontWeight: 600 }}>
              Send
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showReceiptModal} onHide={handleReceiptClose} centered>
          <Modal.Header closeButton>
            <Modal.Title>Transfer Receipt</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {transferReceipt && (
              <div
                ref={receiptRef}
                className="p-3"
                style={{ background: '#f8fafc', borderRadius: '10px' }}
              >
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div className="d-flex align-items-center gap-5">
                    <img
                      src={companyInfo.logo}
                      alt="Company Logo"
                      style={{ width: '80px', height: '80px', objectFit: 'contain' }}
                    />
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#1e293b' }}>
                        {companyInfo.name}
                      </div>
                      <div style={{ color: '#64748b', fontSize: '0.85rem' }}>Transaction Receipt</div>
                    </div>
                  </div>
                  {/* <Badge bg="success" style={{ fontSize: '0.85rem' }}>
                    Success
                  </Badge> */}
                </div>

                <div className="mb-3">
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: '0.85rem'
                    }}
                  >
                    <span style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>Date & Time</span>
                    <span style={{ color: '#475569', textAlign: 'right' }}>{transferReceipt.timestamp}</span>
                  </div>
                </div>

                <div className="mb-3" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: '0.85rem'
                    }}
                  >
                    <span style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>Sender</span>
                    <span style={{ color: '#475569', fontWeight: 600, textAlign: 'right' }}>
                      {transferReceipt.senderName}
                      {transferReceipt.senderMobile && (
                        <span style={{ display: 'block', color: '#94a3b8', fontWeight: 400, fontSize: '0.8rem' }}>
                          {transferReceipt.senderMobile}
                        </span>
                      )}
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: '0.85rem'
                    }}
                  >
                    <span style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>Beneficiary</span>
                    <span style={{ color: '#475569', fontWeight: 600, textAlign: 'right' }}>
                      {transferReceipt.beneficiaryName}
                      {transferReceipt.beneficiaryAccount && (
                        <span style={{ display: 'block', color: '#94a3b8', fontWeight: 400, fontSize: '0.8rem' }}>
                          {transferReceipt.beneficiaryAccount}
                        </span>
                      )}
                    </span>
                  </div>
                </div>

                <div className="mb-3">
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginTop: '0.5rem'
                    }}
                  >
                    <span style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>Total Amount</span>
                    <span style={{ color: '#1d4ed8', fontWeight: 700, fontSize: '1.1rem', textAlign: 'right' }}>
                      ₹{Number(transferReceipt.amount).toLocaleString()}
                      {amountInWords && (
                        <span
                          style={{
                            display: 'block',
                            color: '#475569',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            marginTop: '0.1rem'
                          }}
                        >
                          ({amountInWords})
                        </span>
                      )}
                    </span>
                  </div>
                </div>

                <div className="mb-3">
                  <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: '0.5rem' }}>
                    Details
                  </div>
                  <Table bordered size="sm" className="mb-0" style={{ fontSize: '0.85rem' }}>
                    <thead style={{ background: '#e0e7ff' }}>
                      <tr>
                        <th>Amount</th>
                        <th>Transaction ID</th>
                        <th>Bank Ref</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transferReceipt.amountBreakup && transferReceipt.amountBreakup.length > 0 ? (
                        transferReceipt.amountBreakup.map((row, idx) => (
                          <tr key={row.transactionId || row.reference || idx}>
                            <td>₹{Number(row.amount).toLocaleString()}</td>
                            <td>{row.transactionId || row.reference}</td>
                            <td>{row.bankRef}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={3} className="text-center text-muted">
                            No breakup available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>

                <div style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
                  This is a system generated receipt for your reference.
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={handlePrintReceipt} disabled={!transferReceipt}>
              Print
            </Button>
            <Button variant="secondary" onClick={handleReceiptClose}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </Container>
  );
};

export default FundTransfer;

